import { z } from 'zod';
import { BUILT_IN_CATEGORIES, CUSTOM_CATEGORY_NAME_MAX } from './category.js';
import { PACK_BRANDING_PLACEMENTS, type PackBrandingPlacement } from './pack.js';
import { STICKER_TYPES, type StickerType } from './policy.js';

/**
 * Project lifecycle status.
 *  - DRAFT    : work in progress, not finalized.
 *  - ACTIVE   : a completed / in-use project.
 *  - ARCHIVED : hidden from the main list but preserved.
 */
export const PROJECT_STATUSES = ['DRAFT', 'ACTIVE', 'ARCHIVED'] as const;

export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

/** Public shape of a project returned by the API. */
export interface ProjectDTO {
  id: string;
  ownerId: string;
  title: string;
  description: string | null;
  status: ProjectStatus;
  /**
   * Built-in category id, a LEGACY value from a retired category, or null when
   * unassigned. Passed through from storage VERBATIM — never coerced.
   */
  category: string | null;
  /** Set when the project is filed under one of the owner's own categories. */
  customCategoryId: string | null;
  /** What the pack produces: IMAGE | VIDEO | MIXED. */
  stickerType: StickerType;
  tags: string[];
  /** Free-form JSON canvas holding sticker assets & notes. */
  data: Record<string, unknown>;
  /** ISO timestamp of the last autosave, if any. */
  lastAutosaveAt: string | null;
  /** Telegram sticker set short name once published (else null). */
  stickerSetName: string | null;
  /** Public https://t.me/addstickers/<name> link once published. */
  stickerSetUrl: string | null;
  /** ISO timestamp the pack was published to Telegram, if any. */
  publishedAt: string | null;
  /** Sync state of a published pack (SYNCED | LOCAL_CHANGES | SYNCING | SYNC_FAILED), else null. */
  syncState: string | null;
  /** Telegram ownership model of the set (LEGACY_USER_OWNED | ASTRAL_MANAGED), else null. */
  ownershipMode: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A single point-in-time snapshot in a project's history. */
export interface ProjectRevisionDTO {
  id: string;
  projectId: string;
  /** Restorable snapshot of the project payload (see toRevisionSnapshot). */
  data: Record<string, unknown>;
  /** Short human note describing the change (e.g. "autosave", "manual"). */
  note: string | null;
  createdAt: string;
}

/**
 * A history-list entry. Deliberately carries NO snapshot: the list used to ship
 * every revision's full payload, which for a media-heavy project meant tens of
 * megabytes in one response. The snapshot is fetched per revision, on demand.
 */
export interface ProjectRevisionSummaryDTO {
  id: string;
  projectId: string;
  note: string | null;
  createdAt: string;
  /**
   * Stickers in the revision. Null for rows written before the count was
   * recorded — the UI simply omits the figure for those.
   */
  assetCount: number | null;
}

const tagSchema = z
  .string()
  .trim()
  .min(1)
  .max(24)
  .regex(/^[\p{L}\p{N} _-]+$/u, 'Tags may only contain letters, numbers, spaces, hyphens and underscores.');

const jsonRecordSchema = z.record(z.string(), z.unknown());

// Create asks only what is useful: name, category, sticker type.
export const createProjectSchema = z
  .object({
    title: z.string().trim().min(1).max(80),
    // Optional, and with no default: a new project starts UNCATEGORISED.
    category: z.enum(BUILT_IN_CATEGORIES).optional(),
    customCategoryId: z.string().min(1).optional(),
    stickerType: z.enum(STICKER_TYPES).optional(),
    status: z.enum(PROJECT_STATUSES).optional(),
    tags: z.array(tagSchema).max(20).optional(),
    data: jsonRecordSchema.optional(),
  })
  .strict();

export type CreateProjectInput = z.infer<typeof createProjectSchema>;

export const updateProjectSchema = z
  .object({
    title: z.string().trim().min(1).max(80).optional(),
    description: z.string().trim().max(500).nullable().optional(),
    status: z.enum(PROJECT_STATUSES).optional(),
    /*
     * NULLABLE so a category can be CLEARED. `undefined` means "leave it
     * alone"; `null` means "unassign". Picking a built-in clears any custom
     * assignment and vice versa — see `writeProjectCategory`.
     */
    category: z.enum(BUILT_IN_CATEGORIES).nullable().optional(),
    customCategoryId: z.string().min(1).nullable().optional(),
    stickerType: z.enum(STICKER_TYPES).optional(),
    tags: z.array(tagSchema).max(20).optional(),
    data: jsonRecordSchema.optional(),
  })
  .strict();

export type UpdateProjectInput = z.infer<typeof updateProjectSchema>;

/**
 * Autosave payload. Intentionally minimal: only the volatile canvas data
 * plus an optional note. Autosaves also create a history revision.
 */
export const autosaveProjectSchema = z
  .object({
    data: jsonRecordSchema,
    note: z.string().trim().max(120).optional(),
  })
  .strict();

export type AutosaveProjectInput = z.infer<typeof autosaveProjectSchema>;

/**
 * Initial-publish payload. `shortName` is the user-chosen link prefix (the
 * `_by_<bot>` suffix is appended server-side); `title` is the display title
 * (branding is applied server-side for Astral-managed packs). Both are optional
 * so a retry that resumes an existing set needs no input.
 *
 * `packBranding` is the user's PREFERENCE, carried with the request rather than
 * left to arrive separately through the debounced autosave. The selector lives
 * in the publish sheet itself, so a user who picks a placement and immediately
 * taps Publish used to race their own autosave: the server read the previous
 * value out of `Project.data` and published under it, silently dropping a tag
 * the user had explicitly chosen.
 *
 * It is an INPUT, never a decision. The server still resolves the EFFECTIVE
 * placement from its own role + plan (see `resolvePackBranding`), so a Free or
 * lapsed client sending NONE publishes tagged all the same. Nothing about
 * entitlement travels in this payload.
 */
export const publishProjectSchema = z
  .object({
    shortName: z.string().trim().min(1).max(60).optional(),
    title: z.string().trim().min(1).max(64).optional(),
    packBranding: z.enum(PACK_BRANDING_PLACEMENTS).optional(),
  })
  .strict();

export type PublishProjectInput = z.infer<typeof publishProjectSchema>;

/**
 * Rename an ALREADY-PUBLISHED pack: a new base title and/or a new placement.
 *
 * `title` is the UNBRANDED base — the tag is derived and applied server-side,
 * and `Project.title` only ever stores this base form. Both fields are optional
 * so the sheet can change one without restating the other; a request with
 * neither is a no-op that still reports the current remote title.
 *
 * As with publish, `packBranding` is a preference and entitlement is resolved
 * server-side, so a Free account cannot rename its way out of the tag.
 */
export const editPackTitleSchema = z
  .object({
    title: z.string().trim().min(1).max(80).optional(),
    packBranding: z.enum(PACK_BRANDING_PLACEMENTS).optional(),
  })
  .strict();

export type EditPackTitleInput = z.infer<typeof editPackTitleSchema>;

/** Outcome of a pack-title edit, as returned to the Mini App. */
export interface EditPackTitleResult {
  /** The unbranded base title now stored on the project. */
  title: string;
  /** The full branded title the Telegram set now carries. */
  remoteTitle: string;
  /** The EFFECTIVE placement the server resolved (not necessarily the request's). */
  branding: PackBrandingPlacement;
  /** False when the remote title already matched and no write was needed. */
  changed: boolean;
  /** The base title lost characters to fit Telegram's 64-character limit. */
  trimmed: boolean;
}

/** Query parameters for listing / searching projects. */
export const listProjectsQuerySchema = z
  .object({
    search: z.string().trim().max(80).optional(),
    status: z.enum(PROJECT_STATUSES).optional(),
    // Built-in and custom filters are SEPARATE parameters, so a custom id can
    // never be read as a built-in id (or the reverse).
    category: z.enum(BUILT_IN_CATEGORIES).optional(),
    customCategoryId: z.string().min(1).optional(),
    tag: tagSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).default(0),
  })
  .strict();

export type ListProjectsQuery = z.infer<typeof listProjectsQuerySchema>;

/**
 * Create a custom category. The NAME is validated further server-side against
 * the user's existing categories and the built-in labels — see
 * `validateCustomCategoryName`, which owns the duplicate and reserved rules.
 */
export const createCustomCategorySchema = z
  .object({
    name: z.string().min(1).max(CUSTOM_CATEGORY_NAME_MAX * 4),
  })
  .strict();

export type CreateCustomCategoryInput = z.infer<typeof createCustomCategorySchema>;
